import React from "react";
import viewpaths from "global/constants/ViewPathConstants";
import strings from "global/constants/StringConstants";
import FeatureKeys from "global/constants/FeatureKeys";
import { hasAccessTo, isAdmin } from "layouts/Config";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import {
  BrandingNavIcon,
  DelegationNavIcon,
  DocumentsNavIcon,
  IntegrationsNavIcon,
  PlanNavIcon,
  PoliciesNavIcon,
  ProfileNavIcon,
  RolesNavIcon,
  SecurityNavIcon,
  SsoNavIcon,
  TeamsNavIcon,
  TicketsNavIcon,
} from "screens/Settings/components/SettingsNavIcons";

export interface SettingsNavItem {
  name: string;
  path: string;
  /** Rendered by the caller so each surface can size/stroke it itself. */
  icon: React.ReactElement;
  /** Gates the whole destination page — the caller shows a lock/warning icon
   *  and still navigates there, matching the account dropdown menu. */
  featureKey?: string;
}

export interface SettingsNavSection {
  title: string;
  items: SettingsNavItem[];
}

/**
 * The Settings destinations, in reference order, filtered by what the signed-in
 * user may actually reach.
 *
 * Shared by the desktop/tablet rail (`SettingsSideMenu`) and the phone index
 * (`SettingsIndex`) so the two can never list different sections.
 */
/** Each item literal's `featureKey` is a different string literal type (or
 *  absent), which without this annotation TS widens into a union too varied
 *  for `.filter`/`.map` to stay callable on. */
type NavItemWithCondition = SettingsNavItem & { condition: boolean };

export const useSettingsNavSections = (): SettingsNavSection[] => {
  const sections: { title: string; items: NavItemWithCondition[] }[] = [
    {
      title: "Personal",
      items: [
        {
          name: "My Profile",
          path: viewpaths.myProfileViewPath,
          icon: <ProfileNavIcon />,
          condition: true,
        },
        {
          name: "Security",
          path: viewpaths.securityViewPath,
          icon: <SecurityNavIcon />,
          condition: true,
        },
        {
          name: "Delegation",
          path: viewpaths.delegationViewPath,
          icon: <DelegationNavIcon />,
          condition: isAdmin(),
          featureKey: FeatureKeys.ENVELOPE_DELEGATION,
        },
        {
          name: "Support Tickets",
          path: viewpaths.ticketsViewPath,
          icon: <TicketsNavIcon />,
          condition: true,
        },
      ],
    },
    {
      title: "Organization",
      items: [
        {
          name: "My Plan",
          path: viewpaths.viewMyPlanViewPath,
          icon: <PlanNavIcon />,
          condition: hasAccessTo(
            strings.resources.billing,
            strings.fetchPermission,
          ),
        },
        {
          name: "Roles",
          path: viewpaths.rolesViewPath,
          icon: <RolesNavIcon />,
          condition: hasAccessTo(
            strings.resources.app,
            strings.adminPermission,
          ),
        },
        {
          name: "Teams",
          path: viewpaths.teamViewPath,
          icon: <TeamsNavIcon />,
          condition: hasAccessTo(
            strings.resources.app,
            strings.adminPermission,
          ),
        },
        {
          name: "Workspaces",
          path: viewpaths.workspacesViewPath,
          icon: <WorkspacesOutlinedIcon fontSize="small" />,
          condition: hasAccessTo(strings.resources.app, strings.adminPermission),
          featureKey: FeatureKeys.WORKSPACES,
        },
        {
          name: "Document Settings",
          path: viewpaths.documentSettingsViewPath,
          icon: <DocumentsNavIcon />,
          condition: isAdmin(),
        },
        {
          name: "Branding",
          path: viewpaths.brandingViewPath,
          icon: <BrandingNavIcon />,
          condition: hasAccessTo(
            strings.resources.app,
            strings.adminPermission,
          ),
          featureKey: FeatureKeys.BRANDING_CUSTOM,
        },
        {
          name: "Security Policies",
          path: viewpaths.securityPoliciesViewPath,
          icon: <PoliciesNavIcon />,
          condition: isAdmin(),
        },
        {
          name: "Enterprise SSO",
          path: viewpaths.enterpriseSsoViewPath,
          icon: <SsoNavIcon />,
          condition: isAdmin(),
        },
        {
          name: "Integrations",
          path: viewpaths.apiKeysViewPath,
          icon: <IntegrationsNavIcon />,
          condition: true,
          featureKey: FeatureKeys.API_ACCESS,
        },
      ],
    },
  ];

  return sections
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item) => item.condition)
        .map(({ condition, ...item }) => item),
    }))
    .filter((section) => section.items.length > 0);
};

export default useSettingsNavSections;
